import type {
  BannerActionDetailOut,
  BannerLinkOut,
  BannerSize,
} from '@/api/generated/schemas';
import { DEVICE_WIDTH, GUTTER } from '@/components/mobile_preview/constants';
import { ratioLabel } from '@/components/user_catalog/creative/creative';
import type { PreviewAlertItem } from '@/components/mobile_preview/PreviewAlertList';

/**
 * Carousel constants, pinned from the mobile apps and the feed
 * builder. They are duplicated here on purpose: the preview must
 * drift only when someone consciously re-pins them against the
 * sources below. Single-creative geometry lives in `creative.ts`.
 */

/** Re-exported so callers of the banner preview need only one import. */
export { asDeviceWidth } from '@/components/mobile_preview/constants';
export {
  CORNER_RADIUS,
  cropInfo,
  RATIO,
  ratioLabel,
} from '@/components/user_catalog/creative/creative';
export type { CropInfo } from '@/components/user_catalog/creative/creative';

/** NovBannerCarousel.kt */
export const PAGE_GAP = 8;
/** BannerCarouselOut.build_with_data */
export const MAX_PAGES = 10;
/** CarouselAutoPlayEffect.kt. The app's 500ms tween has no counterpart here:
 * the strip turns pages with native smooth scrolling, whose duration is the
 * browser's. */
export const AUTOPLAY_DWELL_MS = 5000;

/** NovBannerCarousel.kt: page = screen width − both gutters, so the next one peeks. */
export const PAGE_WIDTH = DEVICE_WIDTH - GUTTER * 2;

/** Autoplay wraps; manual navigation does not (mirrors mobile). */
export const nextPage = (page: number, count: number): number =>
  count === 0 ? 1 : (page % count) + 1;

/** Keeps a selection inside a page count that just changed under it. */
export const clampPage = (page: number, count: number): number =>
  count === 0 ? 1 : Math.min(Math.max(page, 1), count);

export interface PreviewEntry {
  /** 1-based display position in the feed, after drops. */
  page: number;
  link: BannerLinkOut;
  action: BannerActionDetailOut;
}

export interface PreviewModel {
  /** What the feed will actually contain, in feed order. */
  entries: PreviewEntry[];
  /** Links whose action has no image — silently dropped by the backend. */
  dropped: BannerLinkOut[];
  /** Renderable entries past the cap of 10. */
  truncated: PreviewEntry[];
  /** Entries whose size differs from the effective one — force-cropped on mobile. */
  mixed: PreviewEntry[];
  /** Entries whose kind has no deeplink — rendered but not clickable. */
  unlinked: PreviewEntry[];
  /** Ratio every page is forced to; null when nothing renders. */
  effectiveSize: BannerSize | null;
  /** 1-based feed position that dictated the ratio. */
  ratioSourcePosition: number | null;
  isCarousel: boolean;
}

/**
 * The backend builds the link and sends `""` for kinds it does not
 * map — the banner then renders but is not clickable.
 */
export const deeplinkOf = (link: BannerLinkOut): string | null =>
  link.deeplink || null;

/** Suffixes resolve against `user_catalog.banners.preview.alerts.*`. */
export type PreviewAlert = PreviewAlertItem;

/** Everything the mobile apps do silently, said out loud. */
export const previewAlerts = (model: PreviewModel): PreviewAlert[] => {
  const alerts: PreviewAlert[] = [];
  const positions = (entries: PreviewEntry[]) =>
    entries.map((e) => e.page).join(', ');

  if (model.entries.length === 0) {
    alerts.push({ level: 'error', key: 'nothing_renders', params: {} });
    return alerts;
  }

  if (model.dropped.length > 0) {
    alerts.push({
      level: 'warning',
      key: 'dropped',
      params: { count: model.dropped.length },
    });
  }

  if (model.truncated.length > 0) {
    alerts.push({
      level: 'warning',
      key: 'truncated',
      params: { count: model.truncated.length, cap: MAX_PAGES },
    });
  }

  if (
    model.mixed.length > 0 &&
    model.effectiveSize &&
    model.ratioSourcePosition
  ) {
    alerts.push({
      level: 'warning',
      key: 'mixed_sizes',
      params: {
        count: model.mixed.length,
        positions: positions(model.mixed),
        ratio: ratioLabel(model.effectiveSize),
        source: model.ratioSourcePosition,
      },
    });
  }

  // Nothing is said about links either way: an unlinked page is a
  // legitimate configuration, and a linked one is the norm.
  return alerts;
};

/** Header badge count — infos are not something to fix. */
export const warningCount = (alerts: PreviewAlert[]): number =>
  alerts.filter((a) => a.level !== 'info').length;

export interface LinkRow {
  link: BannerLinkOut;
  /** 1-based display index over *all* links, gaps in `position` collapsed. */
  position: number;
  action: BannerActionDetailOut | undefined;
  /** Feed page, or null when the action never reaches the feed. */
  page: number | null;
  /** Size differs from the effective one — mobile force-crops this page. */
  sizeClash: boolean;
  /** Built by the backend; null when the kind has no route. */
  deeplink: string | null;
}

/**
 * Table rows: every link, including ones the feed drops — the editor
 * needs to see what they configured, not only what ships.
 */
export const buildLinkRows = (
  links: BannerLinkOut[] | null | undefined,
  actionsById: Map<number, BannerActionDetailOut>,
  model: PreviewModel
): LinkRow[] => {
  const pageByLinkId = new Map(model.entries.map((e) => [e.link.id, e.page]));

  return [...(links ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((link, i) => {
      const action = actionsById.get(link.banner_action_id);
      const page = pageByLinkId.get(link.id) ?? null;
      return {
        link,
        position: i + 1,
        action,
        page,
        sizeClash:
          page !== null &&
          action != null &&
          action.size !== model.effectiveSize,
        deeplink: deeplinkOf(link),
      };
    });
};

export const buildPreviewModel = (
  links: BannerLinkOut[] | null | undefined,
  actionsById: Map<number, BannerActionDetailOut>
): PreviewModel => {
  const ordered = [...(links ?? [])].sort((a, b) => a.position - b.position);

  const dropped: BannerLinkOut[] = [];
  const renderable: { link: BannerLinkOut; action: BannerActionDetailOut }[] =
    [];

  for (const link of ordered) {
    const action = actionsById.get(link.banner_action_id);
    // An action we have not loaded yet is not evidence of a drop.
    if (!action) continue;
    if (!action.image_url) {
      dropped.push(link);
      continue;
    }
    renderable.push({ link, action });
  }

  const all: PreviewEntry[] = renderable.map((r, i) => ({
    page: i + 1,
    ...r,
  }));
  const entries = all.slice(0, MAX_PAGES);
  const truncated = all.slice(MAX_PAGES);

  const effectiveSize = entries[0]?.action.size ?? null;

  return {
    entries,
    dropped,
    truncated,
    mixed: entries.filter((e) => e.action.size !== effectiveSize),
    unlinked: entries.filter((e) => deeplinkOf(e.link) === null),
    effectiveSize,
    ratioSourcePosition: entries[0]?.page ?? null,
    isCarousel: entries.length > 1,
  };
};
